/* ─────────────────────────────────────────────
   next-day-countdown.js  v1
   Upload to: Assets/

   Shared countdown logic for Next Day PCs.
   Reads bank holidays + closure ranges from <html> data attributes,
   finds elements with [data-nextday-countdown], updates them every minute.

   Algorithm:
     1. Find next valid collection day (Mon–Fri, not BH, not closure,
        with 2pm cutoff not yet passed if it's today).
     2. Delivery day = collection day + 1.
     3. Time until cutoff = next collection day's 14:00 Europe/London − now.

   Format options (data-nextday-format):
     full       → "Order within 3h 47m for delivery Tuesday"
     compact    → "3h 47m"
     delivery   → "Tuesday"
     long-date  → "Tuesday 12 May"

   Timezone is forced to Europe/London via Intl.DateTimeFormat,
   so users in other timezones still see correct UK cutoff times.
───────────────────────────────────────────── */

(function () {
  'use strict';

  // ── Load once ──
  // This file is now loaded twice on the Next Day templates: once from inside
  // sections/next-day-lp.liquid (and next-day-customise.liquid), and once
  // globally from snippets/head.liquid so the home page section can use it.
  // Without this guard each copy would start its own one-minute interval,
  // both rewriting the same elements with the same text forever.
  if (window.NEXTDAY_COUNTDOWN_LOADED) return;
  window.NEXTDAY_COUNTDOWN_LOADED = true;

  // ── Config from <html> data attributes (set by theme.liquid from theme settings) ──
  var html = document.documentElement;
  var BANK_HOLIDAYS = (html.getAttribute('data-nextday-bank-holidays') || '')
    .split(',')
    .map(function (s) { return s.trim(); })
    .filter(Boolean);

  // Closure ranges: "2026-12-23..2027-01-02;2027-12-23..2028-01-02"
  var CLOSURE_RANGES = (html.getAttribute('data-nextday-closure-ranges') || '')
    .split(';')
    .map(function (s) { return s.trim(); })
    .filter(Boolean)
    .map(function (range) {
      var parts = range.split('..');
      return parts.length === 2
        ? { start: parts[0].trim(), end: parts[1].trim() }
        : null;
    })
    .filter(Boolean);

  var CUTOFF_HOUR = parseInt(html.getAttribute('data-nextday-cutoff-hour') || '14', 10);

  // ── Day name helpers ──
  var DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // ── Get current date in London timezone as parts ──
  // Returns { y, m, d, hour, minute, second, dayOfWeek }
  // m and d are 1-indexed; dayOfWeek is 0=Sun..6=Sat (matches Date.getDay()).
  function getLondonNow() {
    var now = new Date();
    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, weekday: 'short'
    });
    var parts = fmt.formatToParts(now);
    var lookup = {};
    parts.forEach(function (p) { lookup[p.type] = p.value; });
    var dowMap = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
    return {
      y: parseInt(lookup.year, 10),
      m: parseInt(lookup.month, 10),
      d: parseInt(lookup.day, 10),
      hour: parseInt(lookup.hour === '24' ? '0' : lookup.hour, 10),
      minute: parseInt(lookup.minute, 10),
      second: parseInt(lookup.second, 10),
      dayOfWeek: dowMap[lookup.weekday]
    };
  }

  // ── ISO date string from y/m/d ──
  function toIso(y, m, d) {
    return y + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
  }

  // ── Add days to a y/m/d, return new {y, m, d, dayOfWeek} ──
  // Uses UTC arithmetic to avoid local-tz drift; result is interpreted as a calendar date.
  function addDays(y, m, d, n) {
    var t = Date.UTC(y, m - 1, d) + n * 86400000;
    var dt = new Date(t);
    return {
      y: dt.getUTCFullYear(),
      m: dt.getUTCMonth() + 1,
      d: dt.getUTCDate(),
      dayOfWeek: dt.getUTCDay()
    };
  }

  // ── Check if a date is in any closure range ──
  function isInClosure(iso) {
    for (var i = 0; i < CLOSURE_RANGES.length; i++) {
      var r = CLOSURE_RANGES[i];
      if (iso >= r.start && iso <= r.end) return true;
    }
    return false;
  }

  // ── Working day: Mon–Fri, not BH, not closure ──
  function isWorkingDay(y, m, d, dayOfWeek) {
    if (dayOfWeek === 0 || dayOfWeek === 6) return false; // Sun/Sat
    var iso = toIso(y, m, d);
    if (BANK_HOLIDAYS.indexOf(iso) !== -1) return false;
    if (isInClosure(iso)) return false;
    return true;
  }

  // ── Find next working day strictly after the given date ──
  function nextWorkingDay(y, m, d) {
    var cur = { y: y, m: m, d: d };
    for (var i = 0; i < 60; i++) {
      cur = addDays(cur.y, cur.m, cur.d, 1);
      if (isWorkingDay(cur.y, cur.m, cur.d, cur.dayOfWeek)) return cur;
    }
    // Fallback — should never trigger unless closure data is broken
    return addDays(y, m, d, 1);
  }

  // ── Compute UTC ms for "Y-M-D 14:00 Europe/London" ──
  // Handles BST/GMT automatically by sampling the offset at that local time.
  function londonCutoffUtcMs(y, m, d, hour) {
    // Build a UTC timestamp as if the wall-clock were UTC.
    var asIfUtc = Date.UTC(y, m - 1, d, hour, 0, 0);
    // Find what London says that UTC moment is.
    var probe = new Date(asIfUtc);
    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    var parts = fmt.formatToParts(probe);
    var lookup = {};
    parts.forEach(function (p) { lookup[p.type] = p.value; });
    var londonHour = parseInt(lookup.hour === '24' ? '0' : lookup.hour, 10);
    // Offset (in ms) is (intended local hour − London hour at that UTC instant).
    var offsetMs = (hour - londonHour) * 3600000;
    // Day boundary edge: if London is on the previous/next day, adjust by 24h.
    var londonY = parseInt(lookup.year, 10);
    var londonM = parseInt(lookup.month, 10);
    var londonD = parseInt(lookup.day, 10);
    if (londonY < y || (londonY === y && londonM < m) || (londonY === y && londonM === m && londonD < d)) {
      offsetMs += 86400000;
    } else if (londonY > y || (londonY === y && londonM > m) || (londonY === y && londonM === m && londonD > d)) {
      offsetMs -= 86400000;
    }
    return asIfUtc + offsetMs;
  }

  // ── Main: get delivery info ──
  function getNextDeliveryInfo() {
    var now = getLondonNow();
    var nowUtc = Date.now();

    var todayCutoffUtc = londonCutoffUtcMs(now.y, now.m, now.d, CUTOFF_HOUR);
    var collection;

    if (isWorkingDay(now.y, now.m, now.d, now.dayOfWeek) && nowUtc < todayCutoffUtc) {
      collection = { y: now.y, m: now.m, d: now.d };
    } else {
      collection = nextWorkingDay(now.y, now.m, now.d);
    }

    var collectionCutoffUtc = londonCutoffUtcMs(collection.y, collection.m, collection.d, CUTOFF_HOUR);
    var msUntilCutoff = collectionCutoffUtc - nowUtc;
    if (msUntilCutoff < 0) msUntilCutoff = 0;

    var totalMinutes = Math.floor(msUntilCutoff / 60000);
    var totalHours = Math.floor(totalMinutes / 60);
    var days = Math.floor(totalHours / 24);
    var hours = totalHours % 24;
    var minutes = totalMinutes % 60;

    // Delivery day = collection day + 1 (always Tue–Sat under our rules)
    var delivery = addDays(collection.y, collection.m, collection.d, 1);
    var deliveryDayName = DAY_NAMES[delivery.dayOfWeek];
    var deliveryLongDate = deliveryDayName + ' ' + delivery.d + ' ' + MONTH_NAMES[delivery.m - 1];

    return {
      days: days,
      hours: hours,
      minutes: minutes,
      deliveryDayName: deliveryDayName,
      deliveryLongDate: deliveryLongDate
    };
  }

  // ── Format output for an element ──
  function formatOutput(format, info) {
    var hm = info.hours + 'h ' + info.minutes + 'm';
    var time;
    if (info.days > 0) {
      time = info.days + (info.days === 1 ? ' day ' : ' days ') + hm;
    } else {
      time = hm;
    }
    switch (format) {
      case 'compact':   return time;
      case 'delivery':  return info.deliveryDayName;
      case 'long-date': return info.deliveryLongDate;
      case 'full':
      default:          return 'Order within ' + time + ' for delivery ' + info.deliveryDayName;
    }
  }

  // ── Update all matching elements ──
  function updateAll() {
    var info = getNextDeliveryInfo();
    var els = document.querySelectorAll('[data-nextday-countdown]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var format = el.getAttribute('data-nextday-format') || 'full';
      el.textContent = formatOutput(format, info);
    }
  }

  // ── Init ──
  function init() {
    updateAll();
    // Re-run every minute. A small drift is fine; we don't need second-level precision.
    setInterval(updateAll, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The home page injects its sections after DOMContentLoaded, so a countdown
  // element can appear well after init() has already run and found nothing.
  // Re-check on load, on section reload in the theme editor, and briefly on
  // DOM changes.
  window.addEventListener('load', updateAll);
  document.addEventListener('shopify:section:load', updateAll);

  if ('MutationObserver' in window) {
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        if (document.querySelector('[data-nextday-countdown]')) {
          updateAll();
          mo.disconnect();
        }
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(function () { mo.disconnect(); }, 15000);
  }
})();
